import { createContext, useContext, useReducer, useEffect } from "react";
import customFetch from "../api/customFetch.js"; // הוסף את הimport הזה

const AppContext = createContext();

const initial = {
  // Quiz state (קיים)
  course: null,
  attemptId: null,
  currentIndex: 0,
  answeredCount: 0,
  total: 0,
  answers: {},
  lockedUntilVideoEnds: true,

  // Auth state (חדש) - מומלץ להשבית ולהשתמש רק ב-AuthContext
  user: null,
  isLoggedIn: false,
  authLoading: true,
  authError: null,
};

function reducer(state, action) {
  switch (action.type) {
    // Quiz actions (קיימים)
    case "LOAD_COURSE": {
      // תיקון: בדיקה שהקורס קיים ויש לו שאלות
      const course = action.payload;
      const questions = course?.questions || [];
      return {
        ...state,
        course,
        total: questions.length,
      };
    }
    case "RESET_QUIZ":
      return {
        ...state,
        course: null,
        attemptId: null,
        currentIndex: 0,
        answeredCount: 0,
        total: 0,
        answers: {},
        lockedUntilVideoEnds: true,
      };
    case "SET_ATTEMPT":
      return { ...state, attemptId: action.payload };
    case "UNLOCK_START":
      return { ...state, lockedUntilVideoEnds: false };
    case "SUBMIT_ANSWER": {
      const { index, chosenLabel, isCorrect, explain } = action.payload;
      const already = !!state.answers[index];
      return {
        ...state,
        answers: {
          ...state.answers,
          [index]: { chosenLabel, isCorrect, explain },
        },
        answeredCount: already ? state.answeredCount : state.answeredCount + 1,
      };
    }
    case "NEXT":
      return {
        ...state,
        currentIndex: Math.min(state.currentIndex + 1, (state.total || 1) - 1),
      };

    // Auth actions (חדשים) - מומלץ להשבית
    case "AUTH_LOADING":
      return { ...state, authLoading: action.payload };
    case "AUTH_SUCCESS":
      return {
        ...state,
        user: action.payload,
        isLoggedIn: true,
        authLoading: false,
        authError: null,
      };
    case "AUTH_ERROR":
      return {
        ...state,
        user: null,
        isLoggedIn: false,
        authLoading: false,
        authError: action.payload,
      };
    case "AUTH_LOGOUT":
      return {
        ...state,
        user: null,
        isLoggedIn: false,
        authError: null,
      };

    case "SET_QUIZ_STATE": {
      const { currentIndex, answers, answeredCount } = action.payload;
      return {
        ...state,
        currentIndex: currentIndex || 0,
        answers: answers || {},
        answeredCount: answeredCount || 0,
      };
    }

    default:
      return state;
  }
}

export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initial);

  // בדיקת סטטוס אימות בטעינה ראשונית - מתוקן עם customFetch
  const checkAuthStatus = async () => {
    try {
      dispatch({ type: "AUTH_LOADING", payload: true });

      const response = await customFetch.get("/users/check-auth");
      const data = response.data; // axios מחזיר data ישירות

      if (data.success && data.isAuthenticated) {
        dispatch({ type: "AUTH_SUCCESS", payload: data.user });
      } else {
        dispatch({ type: "AUTH_ERROR", payload: "לא מאומת" });
      }
    } catch (error) {
      console.error("שגיאה בבדיקת אימות:", error);
      dispatch({ type: "AUTH_ERROR", payload: "שגיאת רשת" });
    }
  };

  // פונקציית התחברות - מתוקנת עם customFetch
  const login = async (credentials) => {
    try {
      const response = await customFetch.post("/users/login", credentials);
      const data = response.data;

      if (data.success) {
        dispatch({ type: "AUTH_SUCCESS", payload: data.user });
        return { success: true };
      } else {
        dispatch({ type: "AUTH_ERROR", payload: data.message });
        return { success: false, message: data.message };
      }
    } catch (error) {
      const message = error.response?.data?.message || "שגיאה בהתחברות";
      dispatch({ type: "AUTH_ERROR", payload: message });
      return { success: false, message };
    }
  };

  // פונקציית התנתקות - מתוקנת עם customFetch
  const logout = async () => {
    try {
      await customFetch.post("/users/logout");
    } catch (error) {
      console.error("שגיאה בהתנתקות:", error);
    } finally {
      dispatch({ type: "AUTH_LOGOUT" });
    }
  };

  // הערה: יש לך AuthContext נפרד, כדאי לשקול לבטל את הauth כאן כדי למנוע התנגשויות
  // useEffect(() => {
  //   checkAuthStatus();
  // }, []);

  const contextValue = {
    state,
    dispatch,
    login,
    logout,
    checkAuthStatus,
    isAuthenticated: state.isLoggedIn,
    user: state.user,
    authLoading: state.authLoading,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
